<script>
import { mapGetters } from 'vuex';
import { useReportMetrics } from 'dashboard/composables/useReportMetrics';
import { GROUP_BY_FILTER, METRIC_CHART } from './constants';
import fromUnixTime from 'date-fns/fromUnixTime';
import { diaCurto, mesDoAno, semanaCurta } from './helpers/datas';
import { duracaoLegivel as formatTime } from './helpers/duracao';
import ChartStats from './components/ChartElements/ChartStats.vue';
import BarChart from 'shared/components/charts/BarChart.vue';
import EsqueletoLista from 'dashboard/components-next/EsqueletoLista.vue';

export default {
  components: { ChartStats, BarChart, EsqueletoLista },
  props: {
    groupBy: {
      type: Object,
      default: () => ({}),
    },
    accountSummaryKey: {
      type: String,
      default: 'getAccountSummary',
    },
    summaryFetchingKey: {
      type: String,
      default: 'getAccountSummaryFetchingStatus',
    },
    reportKeys: {
      type: Object,
      default: () => ({
        CONVERSATIONS: 'conversations_count',
        INCOMING_MESSAGES: 'incoming_messages_count',
        OUTGOING_MESSAGES: 'outgoing_messages_count',
        FIRST_RESPONSE_TIME: 'avg_first_response_time',
        RESOLUTION_TIME: 'avg_resolution_time',
        RESOLUTION_COUNT: 'resolutions_count',
        REPLY_TIME: 'reply_time',
      }),
    },
  },
  setup(props) {
    const { calculateTrend, isAverageMetricType } = useReportMetrics(
      props.accountSummaryKey
    );
    return { calculateTrend, isAverageMetricType };
  },
  computed: {
    ...mapGetters({
      accountReport: 'getAccountReports',
    }),
    metrics() {
      const reportKeys = Object.keys(this.reportKeys);
      const infoText = {
        FIRST_RESPONSE_TIME: this.$t(
          `REPORT.METRICS.FIRST_RESPONSE_TIME.INFO_TEXT`
        ),
        RESOLUTION_TIME: this.$t(`REPORT.METRICS.RESOLUTION_TIME.INFO_TEXT`),
      };
      return reportKeys.map(key => ({
        NAME: this.$t(`REPORT.METRICS.${key}.NAME`),
        KEY: this.reportKeys[key],
        DESC: this.$t(`REPORT.METRICS.${key}.DESC`),
        INFO_TEXT: infoText[key],
        TOOLTIP_TEXT: `REPORT.METRICS.${key}.TOOLTIP_TEXT`,
        trend: this.calculateTrend(this.reportKeys[key]),
      }));
    },
  },
  methods: {
    getCollection(metric) {
      if (!this.accountReport.data[metric.KEY]) {
        return {};
      }
      const data = this.accountReport.data[metric.KEY];
      const labels = data.map(element => {
        if (this.groupBy?.period === GROUP_BY_FILTER[2].period) {
          let week_date = new Date(fromUnixTime(element.timestamp));
          const first_day = week_date.getDate() - week_date.getDay();
          const last_day = first_day + 6;
          const week_first_date = new Date(week_date.setDate(first_day));
          const week_last_date = new Date(week_date.setDate(last_day));
          return semanaCurta(week_first_date, week_last_date);
        }
        if (this.groupBy?.period === GROUP_BY_FILTER[3].period) {
          return mesDoAno(fromUnixTime(element.timestamp));
        }
        if (this.groupBy?.period === GROUP_BY_FILTER[4].period) {
          return String(fromUnixTime(element.timestamp).getFullYear());
        }
        return diaCurto(fromUnixTime(element.timestamp));
      });
      const datasets = METRIC_CHART[metric.KEY].datasets.map(dataset => {
        switch (dataset.type) {
          case 'bar':
            return {
              ...dataset,
              yAxisID: 'y',
              label: metric.NAME,
              data: data.map(element => element.value),
            };
          case 'line':
            return {
              ...dataset,
              yAxisID: 'y',
              label: this.metrics[0].NAME,
              data: data.map(element => element.count),
            };
          default:
            return dataset;
        }
      });
      return {
        labels,
        datasets,
      };
    },
    getChartOptions(metric) {
      const options = {
        scales: METRIC_CHART[metric.KEY].scales,
      };

      // Only add tooltip configuration for time-based metrics
      if (this.isAverageMetricType(metric.KEY)) {
        options.plugins = {
          tooltip: {
            callbacks: {
              label: ({ raw, dataIndex }) => {
                return this.$t(metric.TOOLTIP_TEXT, {
                  metricValue: formatTime(raw || 0),
                  conversationCount:
                    this.accountReport.data[metric.KEY][dataIndex]?.count || 0,
                });
              },
            },
          },
        };
      }

      return options;
    },
  },
};
</script>

<template>
  <!-- Cada métrica num cartão do produto (padrão v2, 07/10/2026): o número
       grande em cima, a tendência ao lado, o gráfico embaixo. -->
  <div class="rel-graficos">
    <section
      v-for="metric in metrics"
      :key="metric.KEY"
      class="advup-card rel-grafico"
    >
      <ChartStats
        :metric="metric"
        :account-summary-key="accountSummaryKey"
        :summary-fetching-key="summaryFetchingKey"
      />
      <div class="rel-grafico__area">
        <EsqueletoLista
          v-if="accountReport.isFetching[metric.KEY]"
          :linhas="4"
          :altura="40"
        />
        <BarChart
          v-else-if="accountReport.data[metric.KEY].length"
          :collection="getCollection(metric)"
          :chart-options="getChartOptions(metric)"
        />
        <p v-else class="rel-grafico__vazio">
          <i class="i-lucide-chart-no-axes-column" aria-hidden="true" />
          {{ $t('REPORT.NO_ENOUGH_DATA') }}
        </p>
      </div>
    </section>
  </div>
</template>

<style scoped>
.rel-graficos {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: var(--esp-4);
  margin-top: var(--esp-4);
}
.rel-grafico {
  display: flex;
  flex-direction: column;
  gap: var(--esp-4);
  padding: var(--esp-5);
  min-width: 0;
}
.rel-grafico__area {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 260px;
}
.rel-grafico__area > * {
  width: 100%;
}
.rel-grafico__vazio {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--esp-2);
  margin: 0;
  max-width: 280px;
  font-size: var(--txt-nota);
  text-align: center;
  color: rgb(var(--slate-11));
}
.rel-grafico__vazio > i {
  font-size: 24px;
  color: rgb(var(--slate-9));
}
@media (max-width: 767px) {
  .rel-graficos {
    grid-template-columns: minmax(0, 1fr);
  }
  .rel-grafico__area {
    height: 200px;
  }
}
</style>
